import { useNavigate } from "react-router-dom";
import { LogOut, User, Mail, Phone, MapPin, Store, Globe, Coins } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { LangMenu, CurrencyMenu } from "../components/Navbar";
import SubPageShell from "../components/SubPageShell";

export default function Settings() {
  const { user, logout, isSeller } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  if (!user) {
    return (
      <SubPageShell title={t("settings")} tid="settings-page">
        <div className="flex items-center gap-2 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
          <Globe className="ml-1 h-4 w-4 text-teal-600" />
          <LangMenu />
          <Coins className="ml-2 h-4 w-4 text-teal-600" />
          <CurrencyMenu />
        </div>
        <button
          data-testid="settings-login-button"
          onClick={() => navigate("/login?next=/account/settings")}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-teal-700 py-3.5 text-sm font-bold text-white transition hover:bg-teal-800 active:scale-[0.99]"
        >
          {t("nav_login")}
        </button>
      </SubPageShell>
    );
  }

  const onLogout = () => {
    logout();
    navigate("/");
  };

  const rows = [
    { icon: User, label: t("full_name"), value: user.name },
    { icon: Mail, label: t("email"), value: user.email },
    ...(user.phone ? [{ icon: Phone, label: t("phone"), value: user.phone }] : []),
    ...(user.country ? [{ icon: MapPin, label: t("country"), value: user.country }] : []),
  ];

  return (
    <SubPageShell title={t("settings")} tid="settings-page">
      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 border-b border-slate-50 px-4 py-3.5 last:border-0">
            <r.icon className="h-4 w-4 shrink-0 text-teal-600" />
            <span className="text-xs font-semibold text-slate-400">{r.label}</span>
            <span className="ml-auto truncate text-sm font-semibold text-slate-800">{r.value || "—"}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
        <Globe className="ml-1 h-4 w-4 text-teal-600" />
        <LangMenu />
        <Coins className="ml-2 h-4 w-4 text-teal-600" />
        <CurrencyMenu />
      </div>

      {isSeller && (
        <button
          data-testid="settings-seller-dashboard"
          onClick={() => navigate("/seller")}
          className="mt-4 flex w-full items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-sm ring-1 ring-slate-100 transition hover:ring-teal-300"
        >
          <Store className="h-4 w-4 text-teal-600" />
          <span className="text-sm font-bold text-slate-800">{t("nav_dashboard")}</span>
        </button>
      )}

      <button
        data-testid="account-logout-button"
        onClick={onLogout}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-red-200 bg-white py-3.5 text-sm font-bold text-red-600 transition hover:bg-red-50 active:scale-[0.99]"
      >
        <LogOut className="h-4 w-4" /> {t("nav_logout")}
      </button>
    </SubPageShell>
  );
}
